// The six rungs a person reads. One display layer over every level scale.
//
// The codebase carries FIVE naming systems for the same 1–20 ladder:
//   users LevelPolicy        Novice … Ninja … Titan   (stored on user.level)
//   progression Ladder       Novice … Guru … Sovereign
//   common permissions       NOVICE … MAGISTER … SOVEREIGN
//   users LevelPolicy TIERS  Foundation … Apex
//   accreditation ACC        Candidate / Provisional / Accredited / Senior / Principal
// and the public site publishes a sixth (Novice → Fellow).
//
// None of those identifiers change here — they are persisted, gated on,
// and mirrored across services. What changes is the word a customer or
// reviewer reads: every surface renders the RUNG for a level number, and
// says the number beside it ("Senior · Level 9"). Source of truth:
// docs/brand/MOKSY_LEXICON.md §C4 and i18n/en/lexicon.json WORDS.RUNG.
//
// Rung boundaries line up with the gates the marketplace already uses:
// L6 = MARKETPLACE_ELIGIBLE (Reviewer), L7 = WHOLE_REPORT_REVIEW (Senior),
// L12+ adjudicates and calibrates (Principal).

export const RUNGS = Object.freeze([
  { name: 'Candidate',   min: 0,  max: 0,  plain: 'applying' },
  { name: 'Contributor', min: 1,  max: 5,  plain: 'reviewing under supervision' },
  { name: 'Reviewer',    min: 6,  max: 6,  plain: 'accredited to review alone' },
  { name: 'Senior',      min: 7,  max: 11, plain: 'may sign off a whole Blueprint' },
  { name: 'Principal',   min: 12, max: 16, plain: 'adjudicates and calibrates' },
  { name: 'Fellow',      min: 17, max: 20, plain: 'the top of the Ladder' }
]);

export const RUNG_NAMES = Object.freeze(RUNGS.map((r) => r.name));

/**
 * The level a reviewer's paid work unlocks at, and the rung word for it.
 *
 * The worker ladder (`foundation/progression/src/services/Ladder.js`) turns
 * `paidUnlocked` on at level 5. The reviewer dashboard said so twice, in two
 * vocabularies, four lines apart: "you earn MXP towards **Expert**" (the stored
 * ladder's name) and "Unlocks at **Contributor · Level 5**" (the rung). Same
 * rung, two names, on one screen — which is the five-ladder problem this module
 * exists to end, showing up on the surface it was written for.
 *
 * The number lives here so a surface never types a 5, and the word is always
 * the rung.
 */
export const PAID_UNLOCK_LEVEL = 5;

const toLevelNumber = (level) => {
  const n = Number(level);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(20, Math.trunc(n)));
};

/** The rung record for a level number (0 or null/undefined → Candidate). */
export const rungForLevel = (level) => {
  const n = toLevelNumber(level);
  return RUNGS.find((r) => n >= r.min && n <= r.max) || RUNGS[0];
};

/** Just the word: "Senior". */
export const rungName = (level) => rungForLevel(level).name;

/**
 * What a surface prints: "Senior · Level 9". A Candidate has no level to
 * print, so the rung stands alone. `compact` drops the level everywhere
 * (stat tiles, pills).
 */
export const rungLabel = (level, { compact = false } = {}) => {
  const n = toLevelNumber(level);
  const rung = rungForLevel(n);
  if (compact || n === 0) return rung.name;
  return `${rung.name} · Level ${n}`;
};

/**
 * Resolve a level NAME from any of the stored ladders to its number, so
 * callers holding `user.level = 'Guru'` can still render a rung. Names
 * are matched case-insensitively against every known ladder; unknown
 * names resolve to 0 (Candidate) rather than throwing, because a display
 * helper must never take a page down.
 */
const STORED_LADDERS = [
  // users LevelPolicy / dashboard levelPolicy (Titan) and progression Ladder (Sovereign)
  ['Novice', 'Beginner', 'Intermediate', 'Professional', 'Expert', 'Genius', 'Master', 'Ninja', 'Champion', 'Legend',
   'Guru', 'Sage', 'Maestro', 'Architect', 'Visionary', 'Innovator', 'Mystic', 'Virtuoso', 'Guardian', 'Titan'],
  ['Novice', 'Beginner', 'Intermediate', 'Professional', 'Expert', 'Genius', 'Master', 'Ninja', 'Champion', 'Legend',
   'Guru', 'Sage', 'Maestro', 'Architect', 'Visionary', 'Innovator', 'Mystic', 'Virtuoso', 'Guardian', 'Sovereign'],
  // packages/common permissions/levels.js
  ['Novice', 'Apprentice', 'Initiate', 'Adept', 'Practitioner', 'Reviewer', 'Senior_Reviewer', 'Practitioner_Architect',
   'Lead_Practitioner', 'Track_Owner', 'Guru', 'Sage', 'Architect', 'Principal', 'Distinguished', 'Patron', 'Elder',
   'Custodian', 'Magister', 'Sovereign']
];

export const levelNumberFromName = (name) => {
  if (name == null) return 0;
  if (typeof name === 'number') return toLevelNumber(name);
  const key = String(name).trim().toLowerCase();
  if (!key) return 0;
  // Already a rung name → its floor.
  const rung = RUNGS.find((r) => r.name.toLowerCase() === key);
  if (rung) return rung.min;
  // "L9", "level 9", "9"
  const m = key.match(/^(?:l|level\s*)?(\d{1,2})$/);
  if (m) return toLevelNumber(m[1]);
  for (const ladder of STORED_LADDERS) {
    const idx = ladder.findIndex((n) => n.toLowerCase() === key);
    if (idx >= 0) return idx + 1;
  }
  return 0;
};

/** Rung label for anything: a number, "L9", or a stored level name. */
export const rungLabelFor = (levelOrName, opts) => rungLabel(levelNumberFromName(levelOrName), opts);

export default { RUNGS, RUNG_NAMES, rungForLevel, rungName, rungLabel, levelNumberFromName, rungLabelFor };
